/* eslint-disable max-statements */

import { Button, Center, Flex, Loader } from '@mantine/core';
import { useState } from 'react';

import {
  useAdminCatalogsInfo,
  useAdminImportDuties,
  useAdminImportDutyExports,
  useAdminImportMADBs,
  useAdminImportNomenclatures,
  useAdminPublishDuties,
  useAdminPublishDutyExports,
  useAdminPublishMADBs,
  useAdminPublishNomenclatures,
  useAdminSyncIndexes,
} from '@admin/hooks';
import { SyncingIndicator } from '@components';
import { ContentLayout } from '@features/layout';
import { ImportCatalog } from './ImportCatalog';

export const ManageCatalogs = () => {
  const [syncing, setSyncing] = useState(false);

  const importNomenclaturesMutation = useAdminImportNomenclatures();
  const importDutiesMutation = useAdminImportDuties();
  const importDutyExportsMutation = useAdminImportDutyExports();
  const importMADBsMutation = useAdminImportMADBs();
  const publishNomenclaturesMutation = useAdminPublishNomenclatures();
  const publishDutiesMutation = useAdminPublishDuties();
  const publishDutyExportsMutation = useAdminPublishDutyExports();
  const publishMADBsMutation = useAdminPublishMADBs();
  const syncIndexesMutation = useAdminSyncIndexes();
  const {
    data: { testCollectionsAvailability: draftsAvailability, syncRequired, downloadUrls },
  } = useAdminCatalogsInfo();

  const getLanguage = (formLang) => (formLang === 'default' ? '' : formLang);

  const handleNomenclaturesImport = async (values) => {
    await importNomenclaturesMutation.mutateAsync({
      lang: getLanguage(values.lang),
      file: values.file,
    });
  };

  const handleDutiesImport = async (values) => {
    await importDutiesMutation.mutateAsync({
      file: values.file,
    });
  };

  const handleDutyExportsImport = async (values) => {
    await importDutyExportsMutation.mutateAsync({
      file: values.file,
    });
  };

  const handleMADBsImport = async (values) => {
    await importMADBsMutation.mutateAsync({
      body: { fileURL: values.fileURL },
    });
  };

  const handleNomenclaturesPublish = async (values) => {
    await publishNomenclaturesMutation.mutateAsync({
      body: { lang: getLanguage(values.lang) },
    });
  };

  const handleDutiesPublish = async () => {
    await publishDutiesMutation.mutateAsync({ body: undefined });
  };

  const handleDutyExportsPublish = async () => {
    await publishDutyExportsMutation.mutateAsync({ body: undefined });
  };

  const handleMADBsPublish = async () => {
    await publishMADBsMutation.mutateAsync({ body: undefined });
  };

  const handleSyncIndexes = async () => {
    setSyncing(true);
    await syncIndexesMutation.mutateAsync();
    setSyncing(false);
  };

  if (!draftsAvailability) {
    return (
      <Center style={{ height: '200px' }}>
        <Loader size="xl" />
      </Center>
    );
  }

  return (
    <ContentLayout>
      <ContentLayout.Header>
        <ContentLayout.Title>Manage Catalogs</ContentLayout.Title>
        {syncRequired && (
          <Button type="button" variant="outline" size="sm" onClick={handleSyncIndexes} disabled={syncing}>
            {syncing ? <SyncingIndicator /> : 'Sync indexes'}
          </Button>
        )}
      </ContentLayout.Header>

      <ContentLayout.Content>
        <Flex justify="space-around" px={16} mb={32}>
          <ImportCatalog
            catalogTitle="Import Nomenclatures"
            forMultipleLanguages={true}
            onImport={handleNomenclaturesImport}
            draftAvailability={(lang) => draftsAvailability[`test_nomenclatures${getLanguage(lang)}`]}
            onPublish={handleNomenclaturesPublish}
            actionsDisabled={syncing}
            publishDisabled={syncRequired}
            downloadUrl={downloadUrls.nomenclatures}
          ></ImportCatalog>
          <ImportCatalog
            catalogTitle="Import Duties"
            onImport={handleDutiesImport}
            draftAvailability={(_) => draftsAvailability.test_duties}
            onPublish={handleDutiesPublish}
            actionsDisabled={syncing}
            publishDisabled={syncRequired}
            downloadUrl={downloadUrls.duties}
          ></ImportCatalog>
        </Flex>

        <Flex justify="space-around" px={32}>
          <ImportCatalog
            catalogTitle="Import DutyExport"
            onImport={handleDutyExportsImport}
            draftAvailability={(_) => draftsAvailability.test_dutyexports}
            onPublish={handleDutyExportsPublish}
            actionsDisabled={syncing}
            publishDisabled={syncRequired}
            downloadUrl={downloadUrls.duties}
          ></ImportCatalog>
          <ImportCatalog
            catalogTitle="Import MADB"
            onImport={handleMADBsImport}
            draftAvailability={(_) => draftsAvailability.test_madbs}
            onPublish={handleMADBsPublish}
            type="7z"
            actionsDisabled={syncing}
            publishDisabled={syncRequired}
            downloadUrl={downloadUrls.madb}
            useFileURL={true}
          ></ImportCatalog>
        </Flex>
      </ContentLayout.Content>
    </ContentLayout>
  );
};
